import React from 'react'
import { upgradesEnabled } from '../../../lib/featureFlags'
import { PageShell } from './PageShell'
import { SettingsTabButtonIcon } from '../components/SettingsTabButtonIcon'
import { PlanPickerModal } from '../components/PlanPickerModal'
import { InvoiceList } from '../components/InvoiceList'
import { useDashboard } from '../DashboardContext'
import { openBillingPortal, cancelSubscription as apiCancelSubscription, resumeSubscription as apiResumeSubscription } from '../../../lib/billingApi'
import { formatMoney, formatBillingCycle } from '../../../lib/utils'
import { Skeleton } from '../../../components/ui/Skeleton'
import { apiDelete } from '../../../lib/api'
import { clearAuth } from '../../../lib/authStorage'
import { USER_ME } from '../../../lib/endpoints'
import type { SettingsTabId } from '../types'

// ─── Billing helpers ──────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso))
  } catch {
    return iso
  }
}

// ─── Cancel modal ─────────────────────────────────────────────────────────────

function CancelModal({
  periodEnd,
  onClose,
  onCanceled,
}: {
  periodEnd: string
  onClose: () => void
  onCanceled: () => Promise<void>
}) {
  const [atPeriodEnd, setAtPeriodEnd] = React.useState(true)
  const [canceling, setCanceling] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function handleConfirm() {
    setCanceling(true)
    setError(null)
    try {
      await apiCancelSubscription(atPeriodEnd)
      await onCanceled()
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Failed to cancel subscription')
      setCanceling(false)
    }
  }

  const optionStyle = (selected: boolean, danger = false): React.CSSProperties => ({
    display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px',
    border: `1px solid ${selected ? (danger ? '#dc2626' : 'var(--accent)') : 'var(--border)'}`,
    borderRadius: 8,
    background: selected ? (danger ? 'rgba(220,38,38,0.05)' : 'var(--accent-bg)') : 'var(--surface2)',
  })

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw' }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Cancel subscription</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>Choose when to cancel.</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          <label style={optionStyle(atPeriodEnd)}>
            <input type="radio" checked={atPeriodEnd} onChange={() => setAtPeriodEnd(true)} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Cancel at period end ({formatDate(periodEnd)})</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Keep access until billing period ends. Recommended.</div>
            </div>
          </label>
          <label style={optionStyle(!atPeriodEnd, true)}>
            <input type="radio" checked={!atPeriodEnd} onChange={() => setAtPeriodEnd(false)} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Cancel immediately, no refund</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Access ends right now. This cannot be undone.</div>
            </div>
          </label>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={canceling} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' }}>
            Keep subscription
          </button>
          <button className="btn-danger" onClick={handleConfirm} disabled={canceling} style={{ padding: '8px 16px', fontSize: 13 }}>
            {canceling ? 'Canceling…' : 'Confirm cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Billing section ──────────────────────────────────────────────────────────

function BillingSection() {
  const { currentSubscription, subscriptionLoading, reloadSubscription } = useDashboard()

  const sub = currentSubscription?.subscription ?? null
  const plan = currentSubscription?.plan ?? null
  const usage = currentSubscription?.usage ?? null

  const [portalLoading, setPortalLoading] = React.useState(false)
  const [resumeLoading, setResumeLoading] = React.useState(false)
  const [cancelModalOpen, setCancelModalOpen] = React.useState(false)
  const [planPickerOpen, setPlanPickerOpen] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function handlePortal() {
    setPortalLoading(true)
    setError(null)
    try {
      const { portalUrl } = await openBillingPortal()
      window.location.href = portalUrl
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Failed to open billing portal')
      setPortalLoading(false)
    }
  }

  async function handleResume() {
    setResumeLoading(true)
    setError(null)
    try {
      await apiResumeSubscription()
      await reloadSubscription()
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Failed to resume subscription')
    } finally {
      setResumeLoading(false)
    }
  }

  function priceLabel(): string {
    if (!sub || sub.amountCents === 0) return 'Free'
    return `${formatMoney(sub.amountCents, sub.currency)} / ${formatBillingCycle(sub.billingCycle)}`
  }

  function statusBadge(): { text: string; danger: boolean } | null {
    if (!sub) return null
    if (sub.status === 'TRIALING') return { text: `Trialing · ends ${formatDate(sub.trialEnd)}`, danger: false }
    if (sub.status === 'PAST_DUE') return { text: 'Past due', danger: true }
    if (sub.status === 'GRACE') return { text: `Grace period · ends ${formatDate(sub.gracePeriodEndsAt)}`, danger: true }
    if (sub.status === 'PAUSED') return { text: 'Paused', danger: false }
    if (sub.status === 'CANCELED') return { text: `Canceled · access until ${formatDate(sub.currentPeriodEnd)}`, danger: true }
    if (sub.status === 'EXPIRED') return { text: 'Expired', danger: true }
    if (sub.cancelAtPeriodEnd) return { text: `Cancels ${formatDate(sub.currentPeriodEnd)}`, danger: false }
    return null
  }

  function renewsLine(): string | null {
    if (!sub) return null
    if (sub.status === 'CANCELED' || sub.status === 'EXPIRED') return `Access until ${formatDate(sub.currentPeriodEnd)}`
    if (sub.cancelAtPeriodEnd) return `Cancels on ${formatDate(sub.currentPeriodEnd)}`
    if (sub.status === 'TRIALING') return `Trial ends ${formatDate(sub.trialEnd ?? sub.currentPeriodEnd)}`
    if (sub.status === 'ACTIVE') return `Renews ${formatDate(sub.currentPeriodEnd)}`
    return null
  }

  const contactLimit = usage?.contactLimit ?? -1
  const contactsUsed = usage?.contactsUsed ?? 0
  const usageRatio = contactLimit > 0 ? contactsUsed / contactLimit : 0
  const usageBarColor = usageRatio >= 1 ? 'var(--red, #dc2626)' : usageRatio >= 0.8 ? '#ca8a04' : 'var(--accent)'

  const isActive = sub?.status === 'ACTIVE'
  const isTrialing = sub?.status === 'TRIALING'
  const isPastDueOrGrace = sub?.status === 'PAST_DUE' || sub?.status === 'GRACE'
  const isExpiredOrCanceled = !sub || sub.status === 'CANCELED' || sub.status === 'EXPIRED'

  const badge = statusBadge()

  if (subscriptionLoading) {
    return (
      <div className="settings-section">
        <div className="settings-section-title">Current Plan</div>
        <Skeleton height={88} borderRadius={10} style={{ marginBottom: 16 }} />
        <Skeleton height={36} width={160} borderRadius={8} />
      </div>
    )
  }

  return (
    <>
      <div className="settings-section">
        <div className="settings-section-title">Current Plan</div>

        <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{plan?.displayName ?? 'Free'} Plan</div>
              <div style={{ fontSize: 13, color: 'var(--text2)' }}>{priceLabel()}</div>
            </div>
            {badge && (
              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, background: badge.danger ? 'rgba(220,38,38,0.1)' : 'var(--accent-bg)', color: badge.danger ? 'var(--red, #dc2626)' : 'var(--accent)', fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap', marginLeft: 8 }}>
                {badge.text}
              </span>
            )}
          </div>

          {renewsLine() && <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>{renewsLine()}</div>}

          {contactLimit !== -1 ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                <span>Contacts used</span>
                <span style={{ color: usageBarColor, fontWeight: 500 }}>{contactsUsed} / {contactLimit}</span>
              </div>
              <div style={{ height: 5, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(usageRatio * 100, 100)}%`, background: usageBarColor, borderRadius: 99, transition: 'width 0.3s ease' }} />
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>Unlimited contacts</div>
          )}
        </div>

        {sub?.cancelAtPeriodEnd && sub.status === 'ACTIVE' && (
          <div style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#92400e', marginBottom: 16 }}>
            Your subscription cancels on {formatDate(sub.currentPeriodEnd)}. You keep full access until then.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isExpiredOrCanceled && (
            <button
              className="btn-primary"
              onClick={() => upgradesEnabled && setPlanPickerOpen(true)}
              disabled={!upgradesEnabled || planPickerOpen}
              title={upgradesEnabled ? undefined : 'Upgrades not available yet'}
              style={{ cursor: upgradesEnabled ? undefined : 'not-allowed', opacity: upgradesEnabled ? undefined : 0.5 }}
            >
              Choose a plan
            </button>
          )}
          {(isActive || isTrialing) && !sub?.cancelAtPeriodEnd && (
            <button
              className="btn-primary"
              onClick={() => upgradesEnabled && setPlanPickerOpen(true)}
              disabled={!upgradesEnabled || planPickerOpen}
              title={upgradesEnabled ? undefined : 'Upgrades not available yet'}
              style={{ cursor: upgradesEnabled ? undefined : 'not-allowed', opacity: upgradesEnabled ? undefined : 0.5 }}
            >
              Change plan
            </button>
          )}
          {sub?.cancelAtPeriodEnd && isActive && (
            <button className="btn-primary" onClick={handleResume} disabled={resumeLoading}>
              {resumeLoading ? 'Resuming…' : 'Resume subscription'}
            </button>
          )}
          {(isActive || isPastDueOrGrace) && (
            <button onClick={handlePortal} disabled={portalLoading} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' }}>
              {portalLoading ? 'Opening…' : isPastDueOrGrace ? 'Update payment method' : 'Manage billing'}
            </button>
          )}
          {isActive && !sub?.cancelAtPeriodEnd && (
            <button className="btn-danger" onClick={() => setCancelModalOpen(true)} disabled={cancelModalOpen} style={{ padding: '8px 16px', fontSize: 13 }}>
              Cancel subscription
            </button>
          )}
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 12 }}>
            {error}
          </div>
        )}
      </div>

      <div className="settings-section">
        <div className="settings-section-title">What's included</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
          Every paid plan includes the full nudge sequence, Conversation-Aware AI replies, and recovery alerts.
          Plans differ only by contact cap — upgrade when your list grows.
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Invoices</div>
        <InvoiceList />
      </div>

      {cancelModalOpen && (
        <CancelModal
          periodEnd={sub?.currentPeriodEnd ?? ''}
          onClose={() => setCancelModalOpen(false)}
          onCanceled={async () => { setCancelModalOpen(false); await reloadSubscription() }}
        />
      )}
      {upgradesEnabled && planPickerOpen && <PlanPickerModal onClose={() => setPlanPickerOpen(false)} />}
    </>
  )
}

// ─── Delete account modal ─────────────────────────────────────────────────────

function DeleteAccountModal({ userEmail, onClose }: { userEmail: string; onClose: () => void }) {
  const [inputValue, setInputValue] = React.useState('')
  const [deleting, setDeleting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const confirmed = inputValue.trim() === userEmail

  async function handleDelete() {
    if (!confirmed) return
    setDeleting(true)
    setError(null)
    try {
      await apiDelete(USER_ME)
      clearAuth()
      window.location.href = '/signin'
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Failed to delete account. Please try again.')
      setDeleting(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget && !deleting) onClose() }}
    >
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw' }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#dc2626', marginBottom: 6 }}>Delete account</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20, lineHeight: 1.6 }}>
          This will permanently delete your Nudge account, all contacts, nudge history, and settings. Any active subscription will be canceled immediately. <strong>This cannot be undone.</strong>
        </div>

        <div style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
          To confirm, type your email address: <strong style={{ color: 'var(--text)' }}>{userEmail}</strong>
        </div>

        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder={userEmail}
          disabled={deleting}
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', fontSize: 13, borderRadius: 8, border: `1px solid ${confirmed ? '#dc2626' : 'var(--border)'}`, background: 'var(--surface2)', color: 'var(--text)', outline: 'none', marginBottom: 16 }}
        />

        {error && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={deleting}
            style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: deleting ? 'not-allowed' : 'pointer' }}
          >
            Cancel
          </button>
          <button
            className="btn-danger"
            onClick={handleDelete}
            disabled={!confirmed || deleting}
            style={{ padding: '8px 16px', fontSize: 13, opacity: confirmed ? 1 : 0.4, cursor: confirmed && !deleting ? 'pointer' : 'not-allowed' }}
          >
            {deleting ? 'Deleting…' : 'Delete my account'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Settings page ────────────────────────────────────────────────────────────

const NAV_ITEMS: {
  id: SettingsTabId
  variant: Parameters<typeof SettingsTabButtonIcon>[0]['variant']
  label: string
}[] = [
  { id: 'tab-billing',       variant: 'billing',       label: 'Plan & Billing' },
  { id: 'tab-notifications', variant: 'notifications', label: 'Notifications'  },
  { id: 'tab-danger',        variant: 'danger',        label: 'Danger Zone'    },
]

export function SettingsPage({ isActive }: { isActive: boolean }) {
  const [activeTab, setActiveTab] = React.useState<SettingsTabId>('tab-billing')
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const { me } = useDashboard()

  return (
    <PageShell id="page-settings" isActive={isActive}>
      <div className="settings-tabbar" role="tablist" aria-label="Settings sections">
        {NAV_ITEMS.map(({ id, variant, label }) => (
          <button
            key={id}
            role="tab"
            aria-selected={activeTab === id}
            className={`settings-tabbar-item${activeTab === id ? ' active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <SettingsTabButtonIcon variant={variant} />
            {label}
          </button>
        ))}
      </div>

      <div className="content">
        {activeTab === 'tab-billing' && (
          <div className="settings-panel" role="tabpanel">
            <BillingSection />
          </div>
        )}

        {activeTab === 'tab-notifications' && (
          <div className="settings-panel" role="tabpanel">
            <div className="settings-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div className="settings-section-title" style={{ margin: 0 }}>Email Notifications</div>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--text2)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 7px' }}>COMING SOON</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.5 }}>
                Email notifications are on our roadmap. These settings will become active in a future update.
              </div>
              {[
                { label: 'Customer replied',    sub: 'Get notified when a customer replies to a Nudge' },
                { label: 'Daily lapse summary', sub: 'Morning email with newly lapsed customers'       },
                { label: 'Weekly report',       sub: 'Weekly digest of messages sent and replies'      },
                { label: 'Plan limit warnings', sub: 'Alert when approaching free plan limits'         },
              ].map(row => (
                <div className="toggle-row" key={row.label} style={{ opacity: 0.45, pointerEvents: 'none', userSelect: 'none' }}>
                  <div>
                    <div className="toggle-label">{row.label}</div>
                    <div className="toggle-sub">{row.sub}</div>
                  </div>
                  <div className="toggle-switch" />
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'tab-danger' && (
          <div className="settings-panel" role="tabpanel">
            <div className="settings-section">
              <div className="settings-section-title">Danger Zone</div>
              <div className="danger-zone">
                <div className="danger-title">Delete account</div>
                <div className="danger-desc">Permanently deletes your Nudge account, all contacts, and all message history. This cannot be undone.</div>
                <button
                  className="btn-danger"
                  onClick={() => setDeleteModalOpen(true)}
                  disabled={!me?.email}
                >
                  Delete my account
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {deleteModalOpen && me?.email && (
        <DeleteAccountModal
          userEmail={me.email}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
    </PageShell>
  )
}
